import { type ApiProblem, type ApiSchemas, call } from '@rental/contracts/client'
import { MAINTENANCE_KINDS, VEHICLE_DOCUMENT_KINDS } from '@rental/domain'
import { format, formatDate, formatDateTime, formatEuro, localized } from '@rental/i18n'
import { Button, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { type FormEvent, type ReactNode, useId, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { BookingItem } from '../components/BookingItem'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { ListRow, ROW } from '../components/Results'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { ReasonList, SubPanel } from '../components/SubPanel'
import { Guard, RecordPending } from '../components/states'
import {
  Facts,
  FIELD,
  FIELD_ROW,
  FieldPair,
  Loading,
  PAIRED_FIELD,
  ProblemNotice,
  StaffInput,
  StaffSelect,
} from '../components/ui'
import { useLocale } from '../i18n'
import { VehicleDamages } from '../inspection/VehicleDamages'
import { InterventionTable, NewIntervention } from '../maintenance/Interventions'
import { allTimeTotalCents, vehicleHistoryKey } from '../maintenance/maintenance'
import { problemOf, type StaffProblem } from '../problems'
import { useRomeToday } from '../rome-today'
import { romeInstant, romeNow, roundUp } from '../time'
import { VehicleState } from './Vehicles'

const route = getRouteApi('/vehicles/$vehicleId')

type Detail = ApiSchemas['VehicleDetail']

/**
 * One vehicle, for whoever reads the fleet (UI-2g): anyone else who types its address reads
 * the refusal under its title, and nothing is asked of the server for it.
 */
export function VehicleScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'fleet.read')} title={m.vehicle.title}>
      <VehicleRecord />
    </Guard>
  )
}

/** One vehicle (FLT-2): its documents (FLT-3), blocks (FLT-4) and bookings. */
function VehicleRecord() {
  const { messages: m } = useLocale()
  const { vehicleId } = route.useParams()
  const detail = useQuery({
    queryKey: ['vehicle', vehicleId],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/vehicles/{vehicleId}', {
            params: { path: { vehicleId } },
          }),
        ),
      ),
  })
  const problem = problemOf(detail.error)
  const back = (
    <Link to="/vehicles" className="flex min-h-touch items-center gap-1 self-start">
      <ChevronLeft aria-hidden="true" className="size-4" />
      {m.vehicle.back}
    </Link>
  )
  if (!detail.data) {
    return (
      <RecordPending
        back={back}
        title={m.vehicle.title}
        problem={problem}
        notFound={m.vehicle.notFound}
      />
    )
  }
  return <VehicleDetail detail={detail.data} back={back} problem={problem} />
}

function VehicleDetail({
  detail,
  back,
  problem,
}: {
  detail: Detail
  back: ReactNode
  /** A refresh that failed: said, while the vehicle as last read stays. */
  problem: StaffProblem | null
}) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const { vehicle } = detail
  const notGiven = <span className="text-text-muted">{m.vehicle.notGiven}</span>
  const manage = can(access, 'fleet.manage')
  const today = useRomeToday()
  return (
    <>
      <PageHeader
        back={back}
        title={
          <>
            <span className="figures">{vehicle.plate}</span>
            <span className="text-lg font-regular text-text-muted">
              {' '}
              {vehicle.make} {vehicle.model}
            </span>
          </>
        }
        status={<VehicleState state={vehicle.state} />}
      />
      <ProblemNotice problem={problem} />
      <Section title={m.vehicle.details}>
        <Facts
          items={[
            [m.vehicle.category, localized(vehicle.category.name, locale)],
            [m.vehicle.year, vehicle.year ?? notGiven],
            [m.vehicle.fuel, vehicle.fuel ? m.fuels[vehicle.fuel] : notGiven],
            [
              m.vehicle.transmission,
              vehicle.transmission ? m.transmissions[vehicle.transmission] : notGiven,
            ],
            [m.vehicle.seats, vehicle.seats ?? notGiven],
            [m.vehicle.odometer, vehicle.odometerKm ?? notGiven],
            [m.vehicle.vin, vehicle.vin ?? notGiven],
            [m.vehicle.notes, vehicle.notes ?? notGiven],
          ]}
        />
      </Section>
      {/* One above the other, full width: each holds a form, whose fields pair up only where both fit. */}
      <Section title={m.vehicle.blocks}>
        {detail.blocks.length === 0 ? (
          <p className="text-text-muted">{m.vehicle.noBlocks}</p>
        ) : (
          <ul className="flex flex-col">
            {detail.blocks.map((block) => (
              <BlockRow key={block.id} block={block} vehicleId={vehicle.id} manage={manage} />
            ))}
          </ul>
        )}
        {manage && vehicle.status === 'active' ? <NewBlock vehicleId={vehicle.id} /> : null}
      </Section>
      <Section title={m.vehicle.documents}>
        {detail.documents.length === 0 ? (
          <p className="text-text-muted">{m.vehicle.noDocuments}</p>
        ) : (
          <ul className="flex flex-col">
            {detail.documents.map((document) => (
              <DocumentRow
                key={document.id}
                document={document}
                vehicleId={vehicle.id}
                manage={manage}
                today={today}
              />
            ))}
          </ul>
        )}
        {manage ? <NewDocument vehicleId={vehicle.id} /> : null}
      </Section>
      <Section title={m.vehicle.damages}>
        <VehicleDamages vehicleId={vehicle.id} damages={detail.damages} />
      </Section>
      {manage ? <VehicleInterventions vehicle={vehicle} /> : null}
      <Section title={m.vehicle.bookings}>
        {detail.bookings.length === 0 ? (
          <p className="text-text-muted">{m.vehicle.noBookings}</p>
        ) : (
          <ul className="flex flex-col">
            {detail.bookings.map((row) => (
              <BookingItem key={row.code} row={row} kind="pickup" when="date-time" />
            ))}
          </ul>
        )}
      </Section>
    </>
  )
}

/** Maintenance cost history (managers only): the total is the server's all-time figure. */
function VehicleInterventions({ vehicle }: { vehicle: Detail['vehicle'] }) {
  const { locale, messages: m } = useLocale()
  const [creating, setCreating] = useState(false)
  const history = useQuery({
    queryKey: vehicleHistoryKey(vehicle.id),
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/vehicles/{vehicleId}/interventions', {
            params: { path: { vehicleId: vehicle.id }, query: {} },
          }),
        ),
      ),
  })
  const problem = problemOf(history.error)
  return (
    <Section title={m.maintenance.title}>
      {history.data ? (
        <p className="text-lg">
          <span className="text-text-muted">{m.maintenance.totals.vehicle}: </span>
          <strong className="figures">{formatEuro(allTimeTotalCents(history.data), locale)}</strong>
        </p>
      ) : null}
      <ProblemNotice problem={problem} />
      {!history.data ? (
        problem ? null : (
          <Loading />
        )
      ) : (
        <InterventionTable items={history.data.items} />
      )}
      {creating ? (
        <NewIntervention
          fleet={[vehicle]}
          vehicleId={vehicle.id}
          onClose={() => setCreating(false)}
        />
      ) : (
        <div>
          <Button variant="staff" onClick={() => setCreating(true)}>
            {m.maintenance.form.open}
          </Button>
        </div>
      )}
    </Section>
  )
}

function useRefresh(vehicleId: string) {
  const queryClient = useQueryClient()
  return async () => {
    // The vehicle, the fleet list and every timeline show what changed.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['vehicle', vehicleId] }),
      queryClient.invalidateQueries({ queryKey: ['vehicles'] }),
      queryClient.invalidateQueries({ queryKey: ['timeline'] }),
    ])
  }
}

function BlockRow({
  block,
  vehicleId,
  manage,
}: {
  block: ApiSchemas['VehicleBlock']
  vehicleId: string
  manage: boolean
}) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const refresh = useRefresh(vehicleId)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  // What the button did: once the block is over, the button that did it is gone.
  const [outcome, setOutcome] = useState<'ended' | 'cancelled' | null>(null)
  const cancelDialog = useRef<HTMLDialogElement>(null)
  const now = Date.now()
  const live = !block.cancelledAt && (!block.endsAt || Date.parse(block.endsAt) > now)
  const started = Date.parse(block.startsAt) <= now
  const kind = m.maintenanceKinds[block.kind]

  async function end() {
    // Ended or cancelled as the press finds the block (UI-2g): one that starts while the page
    // stands open is ended, though its button was drawn before it started.
    const startedAtPress = Date.parse(block.startsAt) <= Date.now()
    setBusy(true)
    setProblem(null)
    const result = await call(
      api.POST('/v1/staff/vehicles/{vehicleId}/blocks/{blockId}/end', {
        params: { path: { vehicleId, blockId: block.id } },
      }),
    )
    setBusy(false)
    if (result.ok) {
      cancelDialog.current?.close()
      // Before the refresh takes the button away, so the focus has somewhere to go.
      setOutcome(startedAtPress ? 'ended' : 'cancelled')
      await refresh()
      return
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  return (
    <li className={ROW}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {/* Out of service, as the vehicle's state says it (STATE_TONE): wanting a hand, not wrong. */}
        <StatusChip tone={live ? 'warning' : 'neutral'}>{kind}</StatusChip>
        <span className="figures">
          {formatDateTime(locale, block.startsAt)} –{' '}
          {block.endsAt ? formatDateTime(locale, block.endsAt) : m.timeline.untilFurtherNotice}
        </span>
        {block.cancelledAt ? (
          <span className="text-sm text-text-muted">{m.vehicle.cancelled}</span>
        ) : live ? null : (
          <span className="text-sm text-text-muted">{m.vehicle.ended}</span>
        )}
        {manage && live ? (
          <div className="ms-auto">
            <Button
              variant="outline"
              className="rounded-sm"
              busy={busy}
              // Named by its block: with two of them, two buttons of one name are none.
              aria-label={format(started ? m.vehicle.endBlockLabel : m.vehicle.cancelBlockLabel, {
                kind,
                date: formatDateTime(locale, block.startsAt),
              })}
              onClick={() => {
                // Ending a block that runs is one tap; cancelling one not yet begun removes a
                // record, so it is asked first (D11, widened in OPS-1). Decided as the press
                // finds the block, not as the button was drawn: one that started while the
                // page stood open is ended, not asked about (UI-2g).
                if (Date.parse(block.startsAt) <= Date.now()) void end()
                else {
                  setProblem(null)
                  cancelDialog.current?.showModal()
                }
              }}
            >
              {started ? m.vehicle.endBlock : m.vehicle.cancelBlock}
            </Button>
          </div>
        ) : null}
      </div>
      <p className="text-sm text-text-muted">{block.reason}</p>
      {outcome ? (
        <OutcomeNotice key={outcome}>
          <p>{outcome === 'ended' ? m.vehicle.blockEnded : m.vehicle.blockCancelled}</p>
        </OutcomeNotice>
      ) : null}
      {/* The question before cancelling a planned block; a running one is ended in one tap. */}
      {manage && live && !started ? (
        <ConfirmDialog
          dialogRef={cancelDialog}
          title={m.vehicle.cancelBlockTitle}
          confirmLabel={m.vehicle.cancelBlock}
          cancelLabel={m.vehicle.keep}
          busy={busy}
          onConfirm={() => void end()}
          onClose={() => setProblem(null)}
        >
          <p>
            {format(m.vehicle.cancelBlockBody, {
              kind,
              date: formatDateTime(locale, block.startsAt),
            })}
          </p>
          <p className="text-sm text-text-muted">{block.reason}</p>
          <ProblemNotice problem={problem} />
        </ConfirmDialog>
      ) : null}
      {/* A refusal of the one-tap end shows here; the dialog shows its own. */}
      {started ? <ProblemNotice problem={problem} /> : null}
    </li>
  )
}

/** A reason of at least this many letters: the fines' rule, for every record removed with one. */
const REASON_MIN = 3

/**
 * One document (FLT-3). Facts about the date, no new threshold: its day has passed. Red only
 * for the document in force: one renewed since has done its work ("Sostituito"), one cancelled
 * with a reason (OPS-1) is struck through with its reason and counts nowhere. Who manages the
 * fleet cancels a document typed wrong from its row, after a question that names it (D11,
 * widened to removing a record); the right one is then recorded with the form below.
 */
function DocumentRow({
  document,
  vehicleId,
  manage,
  today,
}: {
  document: ApiSchemas['VehicleDetail']['documents'][number]
  vehicleId: string
  manage: boolean
  today: string
}) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const refresh = useRefresh(vehicleId)
  const dialog = useRef<HTMLDialogElement>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [done, setDone] = useState<number | null>(null)
  const past = document.expiresOn < today
  const voided = document.supersededAt !== null
  const kind = m.vehicleDocuments[document.kind]
  const date = formatDate(locale, document.expiresOn, 'short')
  const text = reason.trim()
  const short = text.length > 0 && text.length < REASON_MIN

  async function confirm() {
    setBusy(true)
    setProblem(null)
    const result = await call(
      api.POST('/v1/staff/vehicles/{vehicleId}/documents/{documentId}/supersede', {
        params: { path: { vehicleId, documentId: document.id } },
        body: { reason: text },
      }),
    )
    setBusy(false)
    if (result.ok) {
      dialog.current?.close()
      setDone(Date.now())
      await refresh()
      return
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  return (
    <ListRow
      title={
        <span className={document.current ? 'font-medium' : 'text-text-muted line-through'}>
          {kind}
        </span>
      }
      end={
        voided ? (
          <StatusChip tone="neutral">{m.vehicle.voided}</StatusChip>
        ) : document.current ? (
          past ? (
            <StatusChip tone="danger">{m.vehicle.expired}</StatusChip>
          ) : null
        ) : (
          <StatusChip tone="neutral">{m.vehicle.superseded}</StatusChip>
        )
      }
    >
      <span className="figures">
        {format(past ? m.vehicle.expiredOn : m.vehicle.expiresOnDate, { date })}
      </span>
      {document.reference ? <span>{document.reference}</span> : null}
      {voided && document.supersededReason ? (
        <span className="text-sm text-text-muted">
          {format(m.vehicle.voidedWhy, { reason: document.supersededReason })}
        </span>
      ) : null}
      {manage && !voided ? (
        <>
          <Button
            variant="outline"
            className="self-start rounded-sm"
            aria-label={format(m.vehicle.correctDocumentLabel, { kind, date })}
            onClick={() => {
              setReason('')
              setProblem(null)
              dialog.current?.showModal()
            }}
          >
            {m.vehicle.correctDocument}
          </Button>
          <ConfirmDialog
            dialogRef={dialog}
            title={m.vehicle.correctDocumentTitle}
            confirmLabel={m.vehicle.correctDocumentConfirm}
            cancelLabel={m.vehicle.keep}
            busy={busy}
            ready={text.length >= REASON_MIN}
            reasons={[format(m.vehicle.correctWhy, { min: REASON_MIN })]}
            onConfirm={() => void confirm()}
          >
            <p>{format(m.vehicle.correctDocumentBody, { kind, date })}</p>
            <Field
              label={m.vehicle.correctReason}
              required
              error={short ? format(m.vehicle.correctWhy, { min: REASON_MIN }) : null}
            >
              {(control) => (
                <StaffInput
                  {...control}
                  maxLength={500}
                  autoComplete="off"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              )}
            </Field>
            <ProblemNotice problem={problem} />
          </ConfirmDialog>
        </>
      ) : null}
      {done ? (
        <OutcomeNotice key={done}>
          <p>{m.vehicle.documentVoided}</p>
        </OutcomeNotice>
      ) : null}
    </ListRow>
  )
}

function NewBlock({ vehicleId }: { vehicleId: string }) {
  const { messages: m } = useLocale()
  const { takeOver } = useAccess()
  const refresh = useRefresh(vehicleId)
  const titleId = useId()
  const reasonsId = useId()
  const [now] = useState(() => romeNow())
  const [kind, setKind] = useState<(typeof MAINTENANCE_KINDS)[number]>('repair')
  const [startDate, setStartDate] = useState(now.date)
  const [startTime, setStartTime] = useState(roundUp(now.time))
  const [endDate, setEndDate] = useState('')
  const [endTime, setEndTime] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  // When it was last done: each new block is a new outcome, said again.
  const [done, setDone] = useState<number | null>(null)

  const startsAt = romeInstant(startDate, startTime)
  // An end date needs its time: "until Wednesday" is not a moment, and a
  // guessed midnight would free the car a day early.
  const endsAt = endDate ? romeInstant(endDate, endTime) : null
  // What the button waits for, in the form's order.
  const reasons = [
    ...(reason.trim().length < 3 ? [m.vehicle.needReason] : []),
    ...(startsAt === null ? [m.vehicle.needStart] : []),
    ...(endDate && endsAt === null ? [m.vehicle.needEndTime] : []),
  ]

  async function submit(event: FormEvent) {
    event.preventDefault()
    setProblem(null)
    setDone(null)
    if (reasons.length > 0 || !startsAt) return
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/vehicles/{vehicleId}/blocks', {
        params: { path: { vehicleId } },
        body: {
          kind,
          startsAt,
          ...(endsAt ? { endsAt } : {}),
          reason: reason.trim(),
        },
      }),
    )
    setBusy(false)
    if (result.ok) {
      setReason('')
      setEndDate('')
      setEndTime('')
      setDone(Date.now())
      await refresh()
      return
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  const busyCodes =
    problem?.kind === 'api' && problem.code === 'not_available'
      ? ((problem.details.bookings as string[] | undefined) ?? [])
      : []
  return (
    <form onSubmit={submit} aria-labelledby={titleId} noValidate>
      <SubPanel title={<span id={titleId}>{m.vehicle.newBlock}</span>}>
        <div className={FIELD_ROW}>
          <Field className={FIELD} label={m.vehicle.kind}>
            {(control) => (
              <StaffSelect
                {...control}
                value={kind}
                onChange={(event) =>
                  setKind(event.target.value as (typeof MAINTENANCE_KINDS)[number])
                }
              >
                {MAINTENANCE_KINDS.map((value) => (
                  <option key={value} value={value}>
                    {m.maintenanceKinds[value]}
                  </option>
                ))}
              </StaffSelect>
            )}
          </Field>
          <Field className={FIELD} label={m.vehicle.reason} required>
            {(control) => (
              <StaffInput
                {...control}
                maxLength={500}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>
        </div>
        <div className={FIELD_ROW}>
          <FieldPair>
            <Field className={PAIRED_FIELD} label={m.vehicle.startDate} required>
              {(control) => (
                <StaffInput
                  {...control}
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              )}
            </Field>
            <Field className={PAIRED_FIELD} label={m.vehicle.startTime} required>
              {(control) => (
                <StaffInput
                  {...control}
                  type="time"
                  step={300}
                  value={startTime}
                  onChange={(event) => setStartTime(event.target.value)}
                />
              )}
            </Field>
          </FieldPair>
        </div>
        <div className={FIELD_ROW}>
          <FieldPair>
            <Field className={PAIRED_FIELD} label={m.vehicle.endDate} hint={m.vehicle.openEnded}>
              {(control) => (
                <StaffInput
                  {...control}
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              )}
            </Field>
            <Field className={PAIRED_FIELD} label={m.vehicle.endTime} required={endDate !== ''}>
              {(control) => (
                <StaffInput
                  {...control}
                  type="time"
                  step={300}
                  value={endTime}
                  onChange={(event) => setEndTime(event.target.value)}
                />
              )}
            </Field>
          </FieldPair>
        </div>
        {busyCodes.length > 0 ? (
          <Notice tone="danger" live="assertive">
            <p>{format(m.vehicle.busy, { codes: busyCodes.join(', ') })}</p>
          </Notice>
        ) : (
          <ProblemNotice problem={problem} />
        )}
        {done === null ? null : (
          <OutcomeNotice key={done}>
            <p>{m.vehicle.blocked}</p>
          </OutcomeNotice>
        )}
        <div className="flex flex-col gap-2">
          <div>
            <Button
              type="submit"
              variant="staff"
              busy={busy}
              disabled={reasons.length > 0 && !busy}
              aria-describedby={reasons.length > 0 ? reasonsId : undefined}
            >
              {m.vehicle.addBlock}
            </Button>
          </div>
          <ReasonList id={reasonsId} reasons={reasons} />
        </div>
      </SubPanel>
    </form>
  )
}

function NewDocument({ vehicleId }: { vehicleId: string }) {
  const { messages: m } = useLocale()
  const { takeOver } = useAccess()
  const refresh = useRefresh(vehicleId)
  const titleId = useId()
  const reasonsId = useId()
  const [kind, setKind] = useState<(typeof VEHICLE_DOCUMENT_KINDS)[number]>('insurance')
  const [expiresOn, setExpiresOn] = useState('')
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [done, setDone] = useState<number | null>(null)
  const reasons = expiresOn ? [] : [m.vehicle.needExpiry]

  async function submit(event: FormEvent) {
    event.preventDefault()
    setProblem(null)
    setDone(null)
    if (!expiresOn) return
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/vehicles/{vehicleId}/documents', {
        params: { path: { vehicleId } },
        body: {
          kind,
          expiresOn,
          ...(reference.trim() ? { reference: reference.trim() } : {}),
        },
      }),
    )
    setBusy(false)
    if (result.ok) {
      setExpiresOn('')
      setReference('')
      setDone(Date.now())
      await refresh()
      return
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  return (
    <form onSubmit={submit} aria-labelledby={titleId} noValidate>
      <SubPanel title={<span id={titleId}>{m.vehicle.newDocument}</span>}>
        <div className={FIELD_ROW}>
          <Field className={FIELD} label={m.vehicle.documentKind}>
            {(control) => (
              <StaffSelect
                {...control}
                value={kind}
                onChange={(event) =>
                  setKind(event.target.value as (typeof VEHICLE_DOCUMENT_KINDS)[number])
                }
              >
                {VEHICLE_DOCUMENT_KINDS.map((value) => (
                  <option key={value} value={value}>
                    {m.vehicleDocuments[value]}
                  </option>
                ))}
              </StaffSelect>
            )}
          </Field>
          <Field className={FIELD} label={m.vehicle.expiresOn} required>
            {(control) => (
              <StaffInput
                {...control}
                type="date"
                value={expiresOn}
                onChange={(event) => setExpiresOn(event.target.value)}
              />
            )}
          </Field>
        </div>
        <Field label={m.vehicle.reference}>
          {(control) => (
            <StaffInput
              {...control}
              maxLength={80}
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          )}
        </Field>
        <ProblemNotice problem={problem} />
        {done === null ? null : (
          <OutcomeNotice key={done}>
            <p>{m.vehicle.documentAdded}</p>
          </OutcomeNotice>
        )}
        <div className="flex flex-col gap-2">
          <div>
            <Button
              type="submit"
              variant="staff"
              busy={busy}
              disabled={reasons.length > 0 && !busy}
              aria-describedby={reasons.length > 0 ? reasonsId : undefined}
            >
              {m.vehicle.addDocument}
            </Button>
          </div>
          <ReasonList id={reasonsId} reasons={reasons} />
        </div>
      </SubPanel>
    </form>
  )
}
